// BlockRenderer — schema-only renderer for the BlockRenderer recipe shape.
//
// Doctrine (mirrors MJOS-028 in the template):
//   • Page content lives in page_content.content as { blocks: [ ... ] }.
//   • Each block is { type, props, children? }.
//   • TYPE keys into a BlockConfig.components map (e.g. "Hero", "Section").
//   • PROPS are the React props for that component, verbatim.
//   • CHILDREN are nested blocks, passed as the parent's React `children`
//     so wrappers like <Section> can host inline content.
//
// The complexity lives in the per-domain block library
// (PlatformBlocks.tsx) — not here.

import type { ComponentType, ReactNode } from "react";

export type Block = {
  type: string;
  props: Record<string, unknown>;
  children?: Block[];
};

export type PageBlocks = { blocks: Block[] };

export type BlockConfig = {
  components: Record<
    string,
    ComponentType<Record<string, unknown> & { children?: ReactNode }>
  >;
};

export function BlockRenderer({
  config,
  blocks,
}: {
  config: BlockConfig;
  blocks: Block[];
}) {
  return (
    <>
      {blocks.map((b, i) => {
        const Component = config.components[b.type];
        if (!Component) return null;
        const childrenEl = b.children?.length ? (
          <BlockRenderer config={config} blocks={b.children} />
        ) : undefined;
        const key = (b.props?.id as string | undefined) ?? `${b.type}-${i}`;
        return (
          <Component key={key} {...b.props}>
            {childrenEl}
          </Component>
        );
      })}
    </>
  );
}
